import React, { useMemo, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import AnnotationsModule from "highcharts/modules/annotations";
import {
  Box,
  Button,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useStyles } from "../../../theme/appStyles";
import { IProductPriceSplineChart } from "../../../Api/Interface/api.interface";
import dayjs from "dayjs";
import {
  useGetAllPurDhashOrderListTimeLineQuery,
  useGetAllPurDhashOrderListTableQuery,
} from "../../../Api/attoDeskApi";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Collapse } from "@mui/material";
import Pagination from "../../../Components/Pagination";

AnnotationsModule(Highcharts);

interface IOrderListRow {
  orderListId: string;
  orderDate: string;
  suppliers: Record<string, number>;
}

const AttendanceTimeline = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showChart, setShowChart] = useState(false);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const {
    data: productStoreHistoryRecordData,
    isLoading: storeRecordIsLoading,
  } = useGetAllPurDhashOrderListTimeLineQuery({
    productDetailsId: "",
  });

  const { data: tableData, isLoading: tableIsLoading } =
    useGetAllPurDhashOrderListTableQuery({
      productDetailsId: "",
    });

  const productHistoryRecordList = useMemo<IProductPriceSplineChart[]>(() => {
    return (productStoreHistoryRecordData?.data ??
      []) as IProductPriceSplineChart[];
  }, [productStoreHistoryRecordData?.data]);

  const parsedSeries: Highcharts.SeriesSplineOptions[] = (
    productHistoryRecordList || []
  ).map((series) => ({
    type: "spline",
    name: series.name,
    data: (series.data || []).map(([dateString, value]) => [
      new Date(dateString).getTime(),
      value,
    ]),
  }));

  const allTimestampsSet = new Set<string>();

  productHistoryRecordList.forEach((series) => {
    (series.data ?? []).forEach(([dateString]) => {
      allTimestampsSet.add(dateString);
    });
  });

  const tableDataHistoryRecordList = useMemo<IOrderListRow[]>(() => {
    return (tableData?.data ?? []) as IOrderListRow[];
  }, [tableData?.data]);

  const supplierColumns = Array.from(
    new Set(
      ((tableData?.data as IOrderListRow[]) ?? []).flatMap((row) =>
        Object.keys(row.suppliers)
      )
    )
  );

  const allTimestamps = Array.from(allTimestampsSet).sort();

  const tableRows = allTimestamps.map((timestamp) => {
    const row: Record<string, any> = { timestamp };

    productHistoryRecordList.forEach((series) => {
      const found = (series.data ?? []).find(([date]) => date === timestamp);
      row[series.name] = found ? found[1] : "-";
    });

    return row;
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const options: Highcharts.Options = {
    chart: {
      type: "spline",
      style: {
        fontFamily: '"Play", sans-serif',
      },
    },
    title: {
      useHTML: true,
      text: `<div class="${classes.h4Quoto}"></div>`,
    },
    xAxis: {
      type: "datetime",
      title: {
        text: `<div class="${classes.h5}">Date</div>`,
      },
      labels: {
        format: "{value:%Y-%m-%d}",
      },
    },
    yAxis: {
      title: {
        text: `<div class="${classes.h5}">Price ($)</div>`,
      },
    },
    tooltip: {
      shared: true,
      xDateFormat: "%Y-%m-%d %H:%M:%S",
      // crosshairs: true,
    },
    plotOptions: {
      spline: {
        marker: {
          enabled: true,
        },
      },
    },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },
    series: parsedSeries,
  };

  return (
    <div
      style={{
        padding: "5px",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box>
        <TableContainer>
          <Box mb={2} textAlign="right">
            <Button
              variant="contained"
              onClick={() => setShowChart(!showChart)}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transition: "transform 0.3s",
                    transform: showChart ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              }
              sx={{
                background: showChart
                  ? "linear-gradient(45deg, #f44336 30%, #ff7961 90%)"
                  : "linear-gradient(45deg, #4caf50 30%, #81c784 90%)",
                color: "#fff",
                fontWeight: "bold",
                textTransform: "none",
                borderRadius: "50px",
                paddingX: 3,
                paddingY: 1.2,
              }}
            >
              {showChart ? "Hide Chart" : "Show Chart"}
            </Button>

            <Collapse in={showChart} timeout={400}>
              {storeRecordIsLoading ? (
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    height: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress />
                </div>
              ) : (
                <div style={{ minWidth: "650px" }}>
                  <HighchartsReact highcharts={Highcharts} options={options} />
                </div>
              )}
            </Collapse>
          </Box>
          {storeRecordIsLoading ? (
            <div
              style={{
                display: "flex",
                width: "100%",
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            <>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell colSpan={2} />
                    <TableCell
                      colSpan={supplierColumns.length + 1} // +1 for total
                      align="center"
                      sx={{ fontWeight: "bold", background: "#f1f1f1" }}
                    >
                      Supplier
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        background: "#fafafa",
                        minWidth: "150px",
                      }}
                    >
                      Order List ID
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: "bold",
                        background: "#fafafa",
                        minWidth: "200px",
                      }}
                    >
                      Date
                    </TableCell>

                    {supplierColumns.map((supplier) => (
                      <TableCell
                        key={supplier}
                        align="right"
                        sx={{
                          fontWeight: "bold",
                          background: "#fafafa",
                          minWidth: "200px",
                        }}
                      >
                        {supplier}
                      </TableCell>
                    ))}

                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: "bold",
                        background: "#fafafa",
                        minWidth: "200px",
                      }}
                    >
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {(tableDataHistoryRecordList ?? [])
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, idx) => {
                      const supplierAmounts = supplierColumns.map(
                        (supplier) => row.suppliers[supplier] ?? 0
                      );
                      const totalAmount = supplierAmounts.reduce(
                        (a, b) => a + b,
                        0
                      );

                      return (
                        <TableRow key={idx} hover>
                          <TableCell>{row.orderListId}</TableCell>
                          <TableCell>
                            {dayjs(row.orderDate).format("MMM DD, YYYY")}
                          </TableCell>

                          {supplierColumns.map((supplier, sIdx) => {
                            const amount = row.suppliers[supplier] ?? 0;
                            const percentage =
                              totalAmount > 0
                                ? (amount / totalAmount) * 100
                                : 0;

                            return (
                              <TableCell
                                key={sIdx}
                                align="right"
                                sx={{ position: "relative", minWidth: 60 }}
                              >
                                {/* Inline percentage bar */}
                                <Box
                                  sx={{
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    height: "100%",
                                    width: `${percentage}%`,
                                    backgroundColor: "rgba(33, 150, 243, 0.2)",
                                    zIndex: 0,
                                  }}
                                />
                                <Box sx={{ position: "relative", zIndex: 1 }}>
                                  {amount > 0
                                    ? `$${amount.toFixed(
                                        2
                                      )} (${percentage.toFixed(0)}%)`
                                    : "-"}
                                </Box>
                              </TableCell>
                            );
                          })}

                          {/* Total column */}
                          <TableCell align="right" sx={{ fontWeight: "bold" }}>
                            {totalAmount > 0
                              ? `$${totalAmount.toFixed(2)}`
                              : "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>

              <Grid container justifyContent="flex-end" pt={4}>
                <Pagination
                  count={tableRows.length}
                  page={page}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handleChangePage}
                />
              </Grid>
            </>
          )}
        </TableContainer>
      </Box>
    </div>
  );
};

export default AttendanceTimeline;
